import { createLogic, createLogicMiddleware } from 'redux-logic'
import { releasePressedKeys } from '../actions/toolbar'
import { APP_STEP, step, message, startSession, startRTC, restartRTC } from './../actions/app'
import { AGENT_TYPE } from './../actions/agent'
import { wssEndpointFetch, wssEndpointFetchIntegration } from '../actions/wss'
import {
  getIceInfo,
  resolveAgentAddress,
  resolveInstalledAgentAddress,
  getUserDetailsFromTokenRequest,
} from '../actions/signaling'
import { closePeerConnection,
  accessRejected, ACCESS_REJECTED,
  accessPending, ACCESS_PENDING,
  accessGranted } from '../actions/webrtcConnection'
import { setDisconnectMessage, gracefulExit, confirmCloseMessage } from './../actions/disconnect'
import {
  stopListenMediaDevicesChange,
  audioChatEnd,
} from './../actions/media'

/* === starting flow ====
index.js
  - configureStore => setup middlewares etc.
lib/routing
  - routing middleware dispatch the route action [routeToOnDemand, routeToInstalledAgent]
logic/login
  - [routeToOnDemand, routeToInstalledAgent]
  - check login token
  -if present, decode it and dispatch saveTokenDetails({ token, userId }
    (AppContainer will be mounted next)
  - if not present the window is redirected to the portal login page, with return url
    the current window url (so after login will return to the onDemand/installedAgent route)
    and AppContainer will be mounted
AppContainer.componentDidMount
  dispatch [startSession] if its on a connection route (onDemand, instaledAgent)
logic/app
  [startSession]
    dispatch [wssEndpointFetch]
logic/fetchWssEndpoint
  [wssEndpointFetch]
    connect to portal
    get ws endpoint
    dispatch [wssEndpointFetchFulfilled]
lib/wss-mw
  [wssEndpointFetchFulfilled]
    create ws, setup handlers
    dispatch [startRTC]
logic/app
  [startRTC]
  dispatch
    APP_STEP.RTC_START
    getIceInfo
    resolveAgentAddress
    getUserDetailsFromTokenRequest // response notifyUserDetails, just saved in signaling state

lib/signaling-mw
  notifyIceServers (getIceInfo response)
  notifyAgentAddress (resolveAgentAddress response)
  - when both fulfilled:
    send wss AgentDetailsRequest

lib/webrtcConnection-mw
  notifyAgentDetails (AgentDetailsRequest response)
  webRTC.createOffer
  ...
*/

// get preliminary data from portal and open the wss
export const startSessionLogic = createLogic({
  type: [startSession],

  process ({ getState }, dispatch, done) {
    const {
      agent,
    } = getState()

    if (agent.type === AGENT_TYPE.CLASSROOM_AGENT) {
      dispatch(message('msg-connecting-unattended'))
      dispatch(step(APP_STEP.CONNECT_TO_PORTAL))
      dispatch(wssEndpointFetchIntegration())
      done()
      return
    }

    dispatch(message('msg-connecting'))
    dispatch(step(APP_STEP.CONNECT_TO_PORTAL))
    // getCmEndpoint && open wss
    dispatch(wssEndpointFetch())
    done()
  }
})

const getStartMessage = ({ unattended, restart }) => {
  return `msg-rtc-${restart ? 're' : ''}connecting${unattended ? '-unattended' : ''}`
}

// create the rtc connection with the ondemnd
export const startRTCLogic = createLogic({
  type: [startRTC],

  process ({ getState }, dispatch, done) {
    const {
      app: { restart },
      agent,
      router: { sessionKey, id },
      login: { token },
    } = getState()
    dispatch(message(getStartMessage({ agentType: agent.type, unattended: agent.unattended, restart })))
    dispatch(step(APP_STEP.RTC_START))
    // start the signaling process
    dispatch(getIceInfo())
    switch (agent.type) {
      case AGENT_TYPE.ON_DEMAND:
        dispatch(resolveAgentAddress(sessionKey))
        break
      case AGENT_TYPE.INSTALLED_AGENT:
      case AGENT_TYPE.CLASSROOM_AGENT:
        dispatch(resolveInstalledAgentAddress(id))
        break
      default:
        dispatch(resolveAgentAddress(sessionKey))
        break
    }
    dispatch(getUserDetailsFromTokenRequest(token))
    done()
  }
})

// close rtc connection (if any) and launch the startRTC step above
export const restartRTCLogic = createLogic({
  type: [restartRTC],

  process ({ getState }, dispatch, done) {
    const { agent: { type: agentType }, audio: { chatStarted } } = getState()
    dispatch(confirmCloseMessage(false))
    dispatch(message(getStartMessage({ agentType, restart: true })))
    dispatch(step(APP_STEP.RTC_RESTARTING))
    if (chatStarted) {
      dispatch(audioChatEnd({ endByPeer: false, restart: true }))
    } else {
      dispatch(stopListenMediaDevicesChange())
    }
    dispatch(releasePressedKeys())
    dispatch(closePeerConnection())
    dispatch(startRTC())
    done()
  }
})

export const confirmAccessLogic = createLogic({
  type: [accessGranted, accessRejected, accessPending],
  latest: true,

  process ({ action }, dispatch, done) {
    if (action.type === ACCESS_REJECTED) {
      dispatch(gracefulExit())
      dispatch(setDisconnectMessage('disconnect-access-denied'))

    } else if (action.type === ACCESS_PENDING) {
      dispatch(message('msg-wait-access'))
    } else {
      // ACCESS_GRANTED (AppContainer will render the OnDEmand component)
      dispatch(step(APP_STEP.RUNNING))
    }
    done()
  }
})

const arrLogic = [startSessionLogic, startRTCLogic, restartRTCLogic, confirmAccessLogic]

export const appLogicMiddleware = createLogicMiddleware(arrLogic)
